package com.kinoshazam.screens.login.compact

import androidx.compose.foundation.Image
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.statusBarsPadding
import androidx.compose.foundation.layout.width
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.Scaffold
import androidx.compose.material3.TopAppBar
import androidx.compose.material3.TopAppBarDefaults
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.focus.FocusRequester
import androidx.compose.ui.focus.focusProperties
import androidx.compose.ui.focus.focusRequester
import androidx.compose.ui.graphics.ColorFilter
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import com.kinoshazam.Navigator
import com.kinoshazam.screens.details.medium.DialogContent
import com.kinoshazam.uiKit.components.textField.compact.CompactTextField
import com.kinoshazam.uiKit.components.textLabel.TextLabel
import com.kinoshazam.uiKit.components.textLabel.TextLabelState
import com.kinoshazam.uiKit.theme.BoldLarge
import com.kinoshazam.uiKit.theme.Medium
import com.kinoshazam.uiKit.theme.UiKitTheme
import com.kinoshazam.view.TextFieldPreset
import kinoshazam.resources.generated.resources.Res
import kinoshazam.resources.generated.resources.left
import kinoshazam.resources.generated.resources.profile_otp_password_message
import kinoshazam.resources.generated.resources.profile_otp_resend
import kinoshazam.resources.generated.resources.profile_otp_resend_after
import kinoshazam.resources.generated.resources.profile_otp_title
import kinoshazam.resources.generated.resources.profile_otp_verification_message
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import org.jetbrains.compose.resources.painterResource
import org.jetbrains.compose.resources.stringResource
import org.koin.compose.viewmodel.koinViewModel
import org.koin.core.parameter.parametersOf
import presentation.screens.otp.OtpMVIAction
import presentation.screens.otp.OtpState
import presentation.screens.otp.OtpState.OtpFlowType
import presentation.screens.otp.OtpViewModel
import utils.FormFactor


@Composable
fun CompactOtpScreenHost(
    formFactor: FormFactor = FormFactor.Compact,
) {
    val viewModel = koinViewModel<OtpViewModel>()
    val state by viewModel.state.collectAsStateWithLifecycle()

    when (formFactor) {
        FormFactor.Expanded -> {
            Scaffold(
                modifier = Modifier
                    .fillMaxSize()
                    .background(UiKitTheme.colors.transparent),
                containerColor = UiKitTheme.colors.transparent,
            ) {
                DialogContent(
                    onClose = { viewModel.onAction(OtpMVIAction.BackClicked) },
                ) {
                    CompactOtpScreen(
                        state = state,
                        onAction = viewModel::onAction,
                        asDialog = true,
                    )
                }
            }
        }

        else -> CompactOtpScreen(
            state = state,
            onAction = viewModel::onAction,
        )
    }
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
private fun CompactOtpScreen(
    state: OtpState,
    asDialog: Boolean = false,
    onAction: (OtpMVIAction) -> Unit,
) {
    Scaffold(
        modifier = Modifier
            .then(if (asDialog) Modifier.fillMaxWidth() else Modifier.fillMaxSize())
            .background(UiKitTheme.colors.color_3),
        containerColor = UiKitTheme.colors.color_3,
        topBar = {
            TopAppBar(
                modifier = if (asDialog) Modifier else Modifier.statusBarsPadding(),
                title = { },
                navigationIcon = {
                    if (!asDialog) {
                        Image(
                            painter = painterResource(Res.drawable.left),
                            colorFilter = ColorFilter.tint(UiKitTheme.colors.color_1),
                            contentDescription = null,
                            modifier = Modifier
                                .padding(horizontal = 16.dp)
                                .clickable {
                                    onAction(OtpMVIAction.BackClicked)
                                }
                        )
                    }
                },
                colors = TopAppBarDefaults.topAppBarColors(
                    containerColor = UiKitTheme.colors.color_3,
                ),
            )
        },
        content = { paddingValues ->
            ContentScreen(
                paddingValues = paddingValues,
                otp = state.otp,
                remainingTime = state.remainingTime,
                isError = state.isError,
                flowType = state.flowType,
                asDialog = asDialog,
                onAction = onAction
            )
        }
    )
}

@Composable
private fun ContentScreen(
    paddingValues: PaddingValues,
    otp: List<String>,
    remainingTime: Int,
    isError: Boolean,
    flowType: OtpFlowType,
    asDialog: Boolean = false,
    onAction: (OtpMVIAction) -> Unit,
) {
    val codeLength = 6

    val focusRequesters = remember { List(codeLength) { FocusRequester() } }
    val coroutineScope = rememberCoroutineScope()


    Column(
        modifier = Modifier
            .then(if (asDialog) Modifier.fillMaxWidth() else Modifier.fillMaxSize())
            .padding(paddingValues)
            .padding(24.dp),
        horizontalAlignment = Alignment.CenterHorizontally
    ) {
        Spacer(Modifier.height(if (asDialog) 16.dp else 40.dp))

        TextLabel(
            state = TextLabelState(
                text = stringResource(Res.string.profile_otp_title),
                color = UiKitTheme.colors.color_2,
                textStyle = BoldLarge
            )
        )

        Spacer(Modifier.height(24.dp))

        Row(
            horizontalArrangement = Arrangement.spacedBy(12.dp)
        ) {
            otp.forEachIndexed { index, item ->
                CompactTextField(
                    modifier = Modifier
                        .width(48.dp)
                        .height(56.dp)
                        .focusRequester(focusRequesters[index])
                        .focusProperties {
                            next =
                                if (index < codeLength - 1) focusRequesters[index + 1] else FocusRequester.Default
                            previous =
                                if (index > 0) focusRequesters[index - 1] else FocusRequester.Default
                        },
                    state = TextFieldPreset.otpState(
                        text = otp[index],
                        isError = isError,
                    ),
                    onValueChanged = { value ->
                        if (value.length <= 1 && value.all { it.isDigit() }) {
                            onAction(OtpMVIAction.OtpChanged(index, value))

                            if (value.isNotEmpty() && index < codeLength - 1) {
                                coroutineScope.launch {
                                    delay(50)
                                    focusRequesters[index + 1].requestFocus()
                                }
                            }
                        }
                    }
                )
            }

            if (isError) {
                focusRequesters.getOrNull(0)?.requestFocus()
            }
        }

        Spacer(Modifier.height(32.dp))

        if (remainingTime <= 0) {
            TextLabel(
                state = TextLabelState(
                    text = stringResource(Res.string.profile_otp_resend),
                    color = UiKitTheme.colors.color_1,
                    textStyle = Medium
                ),
                modifier = Modifier.clickable {
                    onAction(OtpMVIAction.ResendOtp)
                }
            )
        } else {
            TextLabel(
                state = TextLabelState(
                    text = stringResource(Res.string.profile_otp_resend_after, remainingTime),
                    color = UiKitTheme.colors.color_2,
                    textStyle = Medium
                ),
            )
        }

        Spacer(Modifier.height(32.dp))

        val message = when (flowType) {
            OtpFlowType.Verification -> stringResource(Res.string.profile_otp_verification_message)
            OtpFlowType.Password -> stringResource(Res.string.profile_otp_password_message)
        }
        TextLabel(
            state = TextLabelState(
                text = message,
                color = UiKitTheme.colors.color_2,
                textAlign = TextAlign.Center,
            )
        )
    }
}
